import { useCallback, useMemo } from "react";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { getPricebookHistory } from "../../../../services";
import {
  PricebookBranches,
  PricebookHistoryRow,
} from "../../../../types/price-book.type";
import { formatDateTimeBkk } from "../../../../utils/date";
import Skeleton from "../../../ReprintAuth/Skeleton";
import CataloguePagination from "../../shared/Pagination";
import { useSearchablePagedRows } from "../../shared/useSearchablePagedRows";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
  catalogueRowHeight,
} from "../../shared/constants";
import {
  HISTORY_ACTION_LABEL,
  SCRIPT_ACTOR_LABEL,
  describeHistoryEntry,
} from "./historyText";

const COLUMNS = ["วันที่", "ผู้ทำรายการ", "รายการ", "รายละเอียด"];
/** A row holds one line of text unless a long detail wraps. */
const ROW_TEXT_HEIGHT = 22;
const ROW_HEIGHT = catalogueRowHeight(ROW_TEXT_HEIGHT);

interface HistoryTabProps {
  pricebookUuid: string;
  /** Names the branches a branch change records by company id; null while they load. */
  branches: PricebookBranches | null;
}

/** ประวัติ: every write to the book, newest first, paged by the API. */
export default function HistoryTab({ pricebookUuid, branches }: HistoryTabProps) {
  const fetchHistory = useCallback(
    async (page: number, limit: number) => {
      const {
        data: { data },
      } = await getPricebookHistory(pricebookUuid, page, limit);
      return data ?? { rows: [], count: 0 };
    },
    [pricebookUuid],
  );

  const { page, setPage, limit, setLimit, rows, totalCount, loading } =
    useSearchablePagedRows<PricebookHistoryRow>({
      open: true,
      fetchRows: fetchHistory,
    });

  const branchCodeById = useMemo(
    () =>
      new Map(
        (branches?.branches ?? []).map((branch) => [
          branch.company_id,
          branch.branch_code,
        ]),
      ),
    [branches],
  );

  return (
    <Stack gap={1}>
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <Skeleton columns={COLUMNS.length} rows={limit} rowHeight={ROW_HEIGHT} />
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ยังไม่มีประวัติการแก้ไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row, index) => (
                <HistoryRow
                  key={row.uuid}
                  row={row}
                  striped={index % 2 === 1}
                  branchCodeById={branchCodeById}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CataloguePagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Stack>
  );
}

interface HistoryRowProps {
  row: PricebookHistoryRow;
  striped: boolean;
  branchCodeById: ReadonlyMap<string, string>;
}

function HistoryRow({ row, striped, branchCodeById }: HistoryRowProps) {
  const actorName = row.actor_name ?? SCRIPT_ACTOR_LABEL;

  return (
    <TableRow
      sx={{
        bgcolor: (theme) =>
          striped ? STRIPE_BACKGROUND : theme.palette.background.paper,
        "& td": { padding: `${CATALOGUE_CELL_PADDING}px` },
      }}
    >
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        {formatDateTimeBkk(row.created_at)}
      </TableCell>
      <TableCell>{actorName}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        {HISTORY_ACTION_LABEL[row.action]}
      </TableCell>
      <TableCell>{describeHistoryEntry(row, branchCodeById)}</TableCell>
    </TableRow>
  );
}
